import React, { FC, useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { analyticsConfigQuery, orScheduleMetricsQuery } from '@/graph/dashboard';
import { createTheme, makeStyles, ThemeProvider } from '@material-ui/core/styles';
import CssBaseline from '@material-ui/core/CssBaseline';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Chip from '@material-ui/core/Chip';
import queryString from 'query-string';
import GanttChart, { createPhysicianColorMap, ORMetrics, ORScheduleMetrics } from '../../analytics/GanttChart';

// How many OR rows fit per page (landscape A4 / US letter)
const ORS_PER_PAGE = 10;

const lightTheme = createTheme({
  palette: {
    type: 'light',
    background: {
      default: '#ffffff',
      paper: '#f8fafc',
    },
    text: {
      primary: '#1e293b',
      secondary: '#475569',
    },
  },
});

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: '#ffffff',
    padding: theme.spacing(2),
  },
  chartPage: {
    pageBreakBefore: 'always',
  },
  chartContainer: {},
  chartToolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  chartTitleSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  procedureCount: {
    fontWeight: 500,
    color: '#64748b',
    fontSize: '0.875rem',
  },
  pageIndicator: {
    color: '#94a3b8',
    fontSize: '0.75rem',
  },
  legend: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  legendSwatch: {
    display: 'inline-block',
    width: 16,
    height: 8,
    borderRadius: 2,
    marginRight: 6,
    WebkitPrintColorAdjust: 'exact',
    printColorAdjust: 'exact',
  },
  legendSwatchActual: {
    background: '#3b82f6',
  },
  legendSwatchScheduled: {
    background: 'rgba(59,130,246,0.6)',
    height: 4,
  },
  legendDash: {
    display: 'inline-block',
    width: 16,
    height: 0,
    borderTop: '2px dashed #059669',
    marginRight: 6,
  },
}));

function chunkORMetrics(orMetrics: ORMetrics[]): ORMetrics[][] {
  if (orMetrics.length === 0) return [];

  const chunks: ORMetrics[][] = [];
  let offset = 0;
  while (offset < orMetrics.length) {
    chunks.push(orMetrics.slice(offset, offset + ORS_PER_PAGE));
    offset += ORS_PER_PAGE;
  }

  return chunks;
}

const Legend: FC<{ classes: any }> = ({ classes }) => (
  <Box className={classes.legend}>
    <Chip
      size="small"
      label={
        <span>
          <span className={`${classes.legendSwatch} ${classes.legendSwatchActual}`} /> Actual
        </span>
      }
      variant="outlined"
    />
    <Chip
      size="small"
      label={
        <span>
          <span className={`${classes.legendSwatch} ${classes.legendSwatchScheduled}`} /> Scheduled
        </span>
      }
      variant="outlined"
    />
    <Chip
      size="small"
      label={
        <span>
          <span className={classes.legendDash} /> Capacity
        </span>
      }
      variant="outlined"
    />
  </Box>
);

const ORScheduleTimeline: FC = () => {
  const classes = useStyles();
  const queryParams = queryString.parse(location.search);
  const date = queryParams.date as string;

  const analyticsConfig = useQuery(analyticsConfigQuery);
  const fcotsGracePeriod = analyticsConfig?.data?.analyticsConfiguration?.fcotsGracePeriod;

  const { data: metricsData, loading } = useQuery(orScheduleMetricsQuery, {
    variables: {
      date: date || '',
      fcotsGracePeriod,
    },
    skip: !date,
    fetchPolicy: 'network-only',
  });

  const metrics = metricsData?.orScheduleMetrics || null;

  const physicianColorMap = useMemo(() => {
    if (!metrics?.orMetrics) return new Map<string, string>();
    return createPhysicianColorMap(metrics.orMetrics);
  }, [metrics?.orMetrics]);

  const chunks = useMemo(() => {
    if (!metrics?.orMetrics) return [];
    return chunkORMetrics(metrics.orMetrics);
  }, [metrics?.orMetrics]);

  useEffect(() => {
    if (!loading && metrics) {
      document.body.classList.add('readyForPrint');
    }
  }, [loading, metrics]);

  if (loading || !metrics) {
    return null;
  }

  const totalPages = chunks.length;

  return (
    <ThemeProvider theme={lightTheme}>
      <CssBaseline />
      <style>{`@page { size: landscape; margin: 0.5cm; }`}</style>
      <Box className={classes.root}>
        {/* Chart pages */}
        {chunks.map((chunk, pageIndex) => {
          const chunkMetrics: ORScheduleMetrics = {
            ...metrics,
            orMetrics: chunk,
          };

          return (
            <Box key={pageIndex} className={pageIndex > 0 ? classes.chartPage : undefined}>
              <Box className={classes.chartContainer}>
                <Box className={classes.chartToolbar}>
                  <Box className={classes.chartTitleSection}>
                    <Typography variant="h6" style={{ color: '#1e293b' }}>
                      OR Schedule — {date}
                    </Typography>
                    <Typography className={classes.procedureCount}>{metrics.totalProcedures} Procedures</Typography>
                    {totalPages > 1 && (
                      <Typography className={classes.pageIndicator}>
                        (page {pageIndex + 1} of {totalPages})
                      </Typography>
                    )}
                  </Box>
                  <Legend classes={classes} />
                </Box>

                <GanttChart
                  metrics={chunkMetrics}
                  chartStartHour={6}
                  chartEndHour={18}
                  onProcedureHover={() => {}}
                  physicianColorMap={physicianColorMap}
                  lightMode
                />
              </Box>
            </Box>
          );
        })}

        {metrics.orMetrics.length === 0 && (
          <Box p={4} textAlign="center">
            <Typography variant="h6" style={{ color: '#64748b' }}>
              No Procedures Found
            </Typography>
          </Box>
        )}
      </Box>
    </ThemeProvider>
  );
};

export default ORScheduleTimeline;
